import { createHookContext } from '@clover-util/react-context';
import { Bundle, ContentType, ProductModule, SoftwarePlan } from 'api/contentful/contentTypes.cloverdotcom';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { useDCSBundlesQuery } from 'hooks/DCS/useDCSBundlesQuery';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { useDCSSoftwarePlansQuery } from 'hooks/DCS/useDCSSoftwarePlansQuery';
import { useContentful } from 'hooks/useContentful';
import { useDCS } from 'hooks/useDCS';
import { getIncludedProductIds, getRecommendedAddonIds } from 'hooks/useDCS.utils';
import type { ICartPricing, IProductPricing, IProductsWithSoftwarePlan } from 'hooks/useDCSTypes';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import type { FlatEntry } from 'services/cms/flattenContentful';
import {
  IIncludedProduct,
  INSTALLMENT_LENGTHS,
  IRecommendedAddonProduct,
  IProduct,
} from 'services/dcs/bundle';
import { getSoftwarePriceWithAdditionalProducts } from 'utils/DCSUtils';
import { BooleanFilter } from 'utils/TypeUtils';
import { ISwapCartBundleState } from '../../SwapCartBundleModal/SwapCartBundleModal';
import { useContentfulSoftwarePlansQuery } from '../InYourCart/SoftwarePlan/useContentfulSoftwarePlansQuery/useContentfulSoftwarePlansQuery';
import type { Installments } from '../OrderSummary/types';
import type {
  IAddBundleOptions,
  ICartMetadataModel,
  ICartModel,
  IInstallmentPlanModel,
  IProductModel,
  ISoftwarePlanModel,
  IUpsellModel,
  IPromoModel,
} from '../models';
import CartUtilObject, {
  CartURLActionType,
  getCartDataFromLocalStorage,
  getCartURLAction,
  getCartURLWithoutActions,
  setCartDataToLocalStorage,
} from 'components/Cart/utils/Util';
import { initialCartModel } from './CartContext';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { getMerchantProspectData } from 'services/shop/checkout';
import { useFiMerchant } from 'hooks/useFiMerchant';

type ContentBundle = FlatEntry<Bundle>;
type ContentProduct = FlatEntry<ProductModule>;
type ContentSoftwarePlan = FlatEntry<SoftwarePlan>;

export const { useCartContext, CartContextProvider } = createHookContext('CartContext', () => {
  const initialCartData = getCartDataFromLocalStorage();

  const location = useLocation();
  const history = useHistory();

  const [cartData, setCartData] = useState<ICartModel>(initialCartData ?? initialCartModel);
  const [byoUpsellItems, setByoUpsellItems] = useState<IProductModel[]>([]);

  const { isLoading: isUseDCSBundlesQueryLoading, data: dcsBundlesQueryData } = useDCSBundlesQuery();
  const { isLoading: isUseDCSProductsQueryLoading } = useDCSProductsQuery();
  const { isLoading: isUseDCSSoftwarePlansQueryLoading } = useDCSSoftwarePlansQuery();
  const { setMerchantProspectData, setHasFailedFetchingMerchantProspectData } = useFiMerchant();

  const [swapCartBundleState, setSwapCartBundleState] = useState<ISwapCartBundleState | undefined>();
  const [hasUrlActionError, setHasUrlActionError] = useState(false);

  const {
    getFilteredProductPricings,
    getBundleCartPricingById,
    getCustomCartPricing,
    hasError: hasDcsError,
  } = useDCS({});

  const contentfulBundles = useContentful<Bundle>({ contentType: ContentType.BUNDLE });
  const { data: contentfulSoftwarePlans } = useContentfulSoftwarePlansQuery();
  const contentfulProducts = useContentful<ProductModule>({ contentType: ContentType.PRODUCT_MODULE });

  const cartId = cartData.cart.cartMetadata?.id;
  const isPricingDataLoading =
    isUseDCSBundlesQueryLoading || isUseDCSProductsQueryLoading || isUseDCSSoftwarePlansQueryLoading;

  /**
   * Tries to add a bundle by id to the cart.
   * @param bundleId - The id of the bundle to be added.
   * @param options - An object containing additional options:
   *   @param needsConfirm - If set to false, the bundle will be added without confirmation (default: true).
   *   @param needsRedirect - If set to true, the user will be redirected to the /cart page after adding the bundle (default: false).
   *   @param solutionTitle - If set, the solution title will be displayed on the /cart page.
   * @returns true if the bundle is successfully added to the cart or already exists in the cart, false if there is an existing bundle in the cart and needsConfirm is true.
   */
  const addBundleById = (
    bundleId: string,
    options: IAddBundleOptions = {},
    subscriberRefId?: string,
  ): boolean => {
    const { needsConfirm, needsRedirect, solutionTitle } = options;
    const isValidBundleId = dcsBundlesQueryData?.find(bundle => bundle.id === bundleId);

    if (!isValidBundleId) {
      setHasUrlActionError(true);
      return false;
    }

    const cartPricing = getBundleCartPricingById(bundleId);

    // Must overwrite the cart and navigate to /purchase/info if subscriberRefId exists
    if (subscriberRefId) {
      setCartData(prev => emptyCart(prev));
      addToCart({ cartPricing, accessories: [], bundleId, options });
      getMerchantProspectData(subscriberRefId)
        .then(receivedData => {
          setMerchantProspectData(receivedData);
        })
        .catch(() => {
          console.error(`Error fetching merchant prospect Data for subscriberRefId: ${subscriberRefId}`);
          setHasFailedFetchingMerchantProspectData(true);
        })
        .finally(() => {
          history.push(redesignV2RoutePath.PURCHASE + '/banking-offer');
        });
      return true;
    }

    if (cartId && needsConfirm) {
      setSwapCartBundleState({ bundleId, needsRedirect, solutionTitle });
      return false;
    }

    setCartData(prev => emptyCart(prev));

    addToCart({ cartPricing, accessories: [], bundleId, options });

    if (needsRedirect && location.pathname !== redesignV2RoutePath.CART) {
      history.push(redesignV2RoutePath.CART);
    }
    return true;
  };

  const addToCart = ({
    cartPricing,
    accessories,
    bundleId,
    options,
  }: {
    cartPricing?: ICartPricing;
    accessories: IProductModel[];
    bundleId?: string;
    options?: IAddBundleOptions;
  }) => {
    if (!cartPricing) return;

    const productPricings = getProductPricings(cartPricing, getFilteredProductPricings);

    const contentBundle = bundleId
      ? contentfulBundles?.data?.find(({ dcsId }) => dcsId === bundleId)
      : undefined;
    const contentSoftwarePlan = getContentSoftwarePlan(cartPricing, contentfulSoftwarePlans);
    const contentProducts = getContentProducts(cartPricing, contentfulProducts?.data);
    const accessoryContentProducts = getAccessoryContentProducts(accessories, contentfulProducts?.data);

    setCartData(prev =>
      updateCart({
        prev,
        currentPricings: cartPricing,
        productsPricings: productPricings,
        accessories: accessoryContentProducts,
        contentBundle,
        contentSoftwarePlan,
        contentProducts,
        solutionTitle: options?.solutionTitle,
      }),
    );
  };

  const mapUpsellModelsToRecommendedAddonProducts = (
    upsellModels: IUpsellModel[],
  ): IRecommendedAddonProduct[] => {
    const recommendedAddonProducts: IRecommendedAddonProduct[] = [];
    const modelsToProcess: IUpsellModel[] = [...upsellModels];

    while (modelsToProcess.length > 0) {
      const currentUpsell = modelsToProcess.pop()!;

      const product: IProduct = {
        id: currentUpsell.id,
        name: currentUpsell.name,
        fdmpId: currentUpsell.fdmpId,
        msrp: {
          value: currentUpsell.totalCost,
          currencyCode: 'USD',
        },
        isIncludedInMultiDevicePricing: currentUpsell.isIncludedInMultiDevicePricing,
        isSelfServicePurchaseEnabled: true,
        pricing: [],
      };

      const recommendedAddonProduct: IRecommendedAddonProduct = {
        product,
      };

      recommendedAddonProducts.push(recommendedAddonProduct);

      if (currentUpsell.accessories && currentUpsell.accessories.length > 0) {
        modelsToProcess.push(...currentUpsell.accessories);
      }
    }

    return recommendedAddonProducts;
  };

  const setProductsWithSoftwarePlan = (
    productsWithSoftwarePlan: IProductsWithSoftwarePlan,
    accessories: IProductModel[],
    options?: IAddBundleOptions,
  ) => {
    setCartData(prev => emptyCart(prev));
    const cartPricing = getCustomCartPricing(productsWithSoftwarePlan);

    const recommendedAddons: IRecommendedAddonProduct[] =
      mapUpsellModelsToRecommendedAddonProducts(byoUpsellItems);
    const updatedCartPricingForByo = { ...cartPricing, recommendedAddons } as ICartPricing;

    addToCart({ cartPricing: updatedCartPricingForByo, accessories, options });
  };

  /**
   * try to add a Product + SoftwarePlan
   * @param productsWithSoftwarePlan the Product + SoftwarePlan to be added
   * @param accessories the accessories to be added
   * @param needsConfirm if false the bundle will be added without confirmation
   * @param needsRedirect if true the user will be redirected to the /cart page
   * @param solutionTitle if set, the solution title will be placed on the /cart page
   * @returns true if the bundle is successfully added into the cart or already exists in the cart, false if there is an existing bundle in the cart
   */
  const addProductsWithSoftwarePlan = (
    productsWithSoftwarePlan: IProductsWithSoftwarePlan,
    accessories: IProductModel[],
    { needsConfirm, needsRedirect, solutionTitle }: IAddBundleOptions = {},
  ): boolean => {
    if (needsConfirm && cartId) {
      setSwapCartBundleState({ productsWithSoftwarePlan, needsRedirect, solutionTitle, accessories });
      return false;
    }
    setProductsWithSoftwarePlan(productsWithSoftwarePlan, accessories);
    if (needsRedirect && location.pathname !== redesignV2RoutePath.CART) {
      history.push(redesignV2RoutePath.CART);
    }
    return true;
  };

  /**
   * Check if the nextLocation has a valid cart action
   * @param nextLocation location where the user is navigating to
   * @returns true if the user navigation can continue, either because there was no actions to execute or the action was successfully executed.
   * returns false if the action execution fails
   */
  const checkIfTheUserCanNavigate = (nextLocation: any): boolean => {
    const cartAction = getCartURLAction({
      ...nextLocation,
      origin: window.location.origin,
    });

    if (!cartAction) return true;

    return executeCartAction(cartAction);
  };

  /**
   * execute a given cart action
   * @param cartAction action to execute
   * @returns true if the action is executed successfully
   */
  const executeCartAction = (cartAction: CartURLActionType): boolean => {
    if (!cartAction || isPricingDataLoading) return false;

    if (cartAction.action === 'addBundle') {
      return addBundleById(
        cartAction.bundleId,
        { needsConfirm: true, needsRedirect: true },
        cartAction.subscriberRefId,
      );
    }

    if (cartAction.action === 'addProduct') {
      return addProductsWithSoftwarePlan({ planId: cartAction.planId, products: cartAction.products }, [], {
        needsConfirm: true,
        needsRedirect: true,
      });
    }

    return false;
  };

  /**
   * Check if the current location has a valid cart action
   * @returns the cart action if there is a valid action, undefined if there is no action or false if there is an invalid action
   */
  const checkForCartAction = (): CartURLActionType | undefined => {
    const cartAction = CartUtilObject.getCartURLAction();
    if (cartAction === false) return;
    if (!cartAction || !cartAction.action) throw new Error();

    return cartAction;
  };

  useEffect(() => {
    if (!location.search || isPricingDataLoading) return;

    try {
      const cartAction = checkForCartAction();
      cartAction && executeCartAction(cartAction);
    } catch (e) {
      console.error(e);
      setHasUrlActionError(true);
    } finally {
      const newCartURL = getCartURLWithoutActions();
      if (newCartURL) {
        history.replace(newCartURL);
      }
    }
  }, [location.search, cartId, isPricingDataLoading, history]);

  useDeepCompareEffect(() => {
    setCartDataToLocalStorage(cartData);
    setByoUpsellItems([]); //clears the state after the cart page is rendered
  }, [cartData]);

  return {
    ...cartData,
    addBundleById,
    addProductsWithSoftwarePlan,
    hasUrlActionError,
    isPricingDataLoading,
    swapCartBundleState,
    setSwapCartBundleState,
    cartId,
    checkIfTheUserCanNavigate,
    hasDcsError,
    emptyCart: () => setCartData(emptyCart),
    addProduct: (product: IUpsellModel) => setCartData(prev => addProduct(product, prev)),
    updateProductQuantityById: (productId: string, quantity: number) =>
      setCartData(prev => updateProductQuantityById(productId, quantity, prev)),
    removeProductById: (productId: string) => setCartData(prev => removeProductById(productId, prev)),
    setTaxRate: (tax: number) => setCartData(prev => setTaxRate(tax, prev)),
    addPromo: (promo: IPromoModel) => setCartData(prev => addPromo(promo, prev)),
    removePromo: () => setCartData(prev => removePromo(prev)),
    clearConflicts: () => setCartData(prev => ({ ...prev, productsWithRemovalConflict: [] })),
    setByoUpsellItems,
  };
});

const getProductPricings = (
  cartPricing: ICartPricing,
  getFilteredProductPricings: (productIds: string[]) => IProductPricing[],
) => {
  const includedProductIds = getIncludedProductIds(cartPricing);
  const addonProductIds = getRecommendedAddonIds(cartPricing);
  return getFilteredProductPricings([...includedProductIds, ...addonProductIds]);
};

export const getContentSoftwarePlan = (
  cartPricing: ICartPricing,
  contentfulSoftwarePlans?: FlatEntry<SoftwarePlan>[],
) => {
  return contentfulSoftwarePlans?.find(plan => plan.dcsId === cartPricing.softwarePlan.id);
};

const getContentProducts = (cartPricing: ICartPricing, contentfulProducts?: FlatEntry<ProductModule>[]) => {
  const includedProductIds = getIncludedProductIds(cartPricing);
  const addonProductIds = getRecommendedAddonIds(cartPricing);
  return contentfulProducts?.filter(({ dcsId }) => {
    if (!dcsId) return false;
    return includedProductIds.includes(dcsId) || addonProductIds.includes(dcsId);
  });
};

const getAccessoryContentProducts = (
  accessories: IProductModel[],
  contentfulProducts?: FlatEntry<ProductModule>[],
): IProductModel[] | undefined => {
  if (!contentfulProducts) return undefined;

  return accessories.map(accessory => {
    const contentProduct = contentfulProducts.find(({ dcsId }) => dcsId === accessory.id);
    if (contentProduct) {
      return hydrateProductWithContent(contentProduct, accessory);
    }
    return accessory;
  });
};

const updateCheckout = (prev: ICartModel): ICartModel => {
  const products = [...(prev.cart.cartMetadata?.hardware || []), ...prev.cart.products];
  const count = products.reduce((total, { quantity }) => total + quantity, 0);
  const subtotal = products.reduce((total, { totalCost, quantity }) => total + totalCost * quantity, 0);
  const allProducts = [...(prev.cart.cartMetadata?.hardware || []), ...prev.cart.products];
  const softwareTotal = getSoftwarePriceWithAdditionalProducts(
    allProducts,
    prev.cart.softwarePlan?.additionalDevicePrice ?? 0,
    prev.cart.softwarePlan?.monthly ?? 0,
  );
  const installmentPlans = updateInstallmentPlans(products, prev.checkout.installmentPlans);

  return {
    ...prev,
    cart: { ...prev.cart },
    checkout: { ...prev.checkout, count, subtotal, softwareTotal, installmentPlans },
  };
};

const updateInstallmentPlans = (
  products: IProductModel[],
  installmentPlans: IInstallmentPlanModel[],
): IInstallmentPlanModel[] => {
  return installmentPlans.map(plan => {
    const planWithQuantity = products.map(product => ({
      quantity: product.quantity,
      plans: product.installmentPlans,
    }));
    const totalCost = planWithQuantity?.reduce(
      (total, pro) => total + pro.quantity * pro?.plans?.[plan.id]?.value || total,
      0,
    );

    return {
      ...plan,
      cost: totalCost,
    };
  });
};

const emptyCart = (prev: ICartModel): ICartModel => {
  const { tax } = prev.checkout;
  return {
    productsWithRemovalConflict: [],
    cart: initialCartModel.cart,
    checkout: { ...initialCartModel.checkout, tax },
    hasUrlActionError: false,
    isPricingDataLoading: false,
    byoUpsellItems: [],
  };
};

const addProduct = (product: IUpsellModel, prev: ICartModel): ICartModel => {
  const products = [...prev.cart.products];
  const index = products.findIndex(p => p.id === product.id);
  if (index === -1) {
    products.push({ ...product, quantity: 1 });
  } else {
    products[index] = { ...products[index], quantity: products[index].quantity + 1 };
  }
  const upsell = prev.cart.upsell.filter(u => u.id !== product.id);
  return updateCheckout({ ...prev, cart: { ...prev.cart, products, upsell } });
};

const updateProductQuantityById = (productId: string, quantity: number, prev: ICartModel): ICartModel => {
  const updateQuantity = (product: IProductModel) => ({
    ...product,
    quantity: product.id === productId ? quantity : product.quantity,
  });
  const products = prev.cart.products.map(updateQuantity);
  const hardware = prev.cart.cartMetadata?.hardware.map(updateQuantity);
  const cartMetadata = prev.cart.cartMetadata && hardware ? { ...prev.cart.cartMetadata, hardware } : null;
  return updateCheckout({ ...prev, cart: { ...prev.cart, cartMetadata, products } });
};

const removeProductById = (productId: string, prev: ICartModel): ICartModel => {
  const { products, upsell } = prev.cart.products.reduce(
    (acc, product) => {
      if (productId === product.id) {
        const { quantity, ...rest } = product;
        acc.upsell.push(rest);
      } else {
        acc.products.push({ ...product });
      }
      return acc;
    },
    {
      products: [] as IProductModel[],
      upsell: [...prev.cart.upsell] as IUpsellModel[],
    },
  );

  return updateCheckout({ ...prev, cart: { ...prev.cart, products, upsell } });
};

const setTaxRate = (tax: number, prev: ICartModel): ICartModel => {
  return { ...prev, checkout: { ...prev.checkout, tax } };
};

const addPromo = (promo: IPromoModel, prev: ICartModel): ICartModel => {
  const mergedPromo = { ...prev.checkout.promo, ...promo };
  return {
    ...prev,
    checkout: {
      ...prev.checkout,
      promo: { ...mergedPromo, applied: mergedPromo.applied || mergedPromo.program },
    },
  };
};

const removePromo = (prev: ICartModel): ICartModel => {
  return {
    ...prev,
    checkout: { ...prev.checkout, promo: { ...prev.checkout.promo, applied: prev.checkout.promo.program } },
  };
};

const convertAddonToUpsell = (
  { product: { id, name, fdmpId, isIncludedInMultiDevicePricing } }: IRecommendedAddonProduct,
  productsPricings: IProductPricing[],
  contentProducts: ContentProduct[] = [],
): IUpsellModel | undefined => {
  const productsPricing = productsPricings.find(p => p.id === id);
  if (!productsPricing) return;
  const contentProduct = contentProducts.find(p => p.dcsId === id);
  return {
    id,
    fdmpId,
    name: contentProduct?.name || name,
    imageURL: contentProduct?.images?.[0]?.file?.url || null,
    totalCost: productsPricing.basePrice.value,
    isIncludedInMultiDevicePricing,
    installmentPlans: productsPricing.installmentPlans,
    accessories: productsPricing.accessories
      ?.map(a =>
        convertAddonToUpsell({ product: a } as IRecommendedAddonProduct, productsPricings, contentProducts),
      )
      .filter(BooleanFilter),
  };
};

const convertIncludedProductToProduct = (
  { quantity, ...rest }: IIncludedProduct,
  productsPricings: IProductPricing[],
  contentProducts?: ContentProduct[],
): IProductModel | undefined => {
  const product = convertAddonToUpsell(rest, productsPricings, contentProducts);
  if (!product) return;
  return { ...product, quantity };
};

export const hydrateProductWithContent = (
  contentProduct: FlatEntry<ProductModule>,
  accessory: IProductModel,
): IProductModel => {
  return {
    ...accessory,
    name: contentProduct?.name || accessory.name,
    imageURL: contentProduct.images?.[0]?.file?.url || null,
  };
};

const updateCart = ({
  prev,
  currentPricings,
  productsPricings,
  accessories = [],
  contentBundle,
  contentSoftwarePlan,
  contentProducts,
  solutionTitle,
}: {
  prev: ICartModel;
  currentPricings: ICartPricing;
  productsPricings: IProductPricing[];
  accessories?: IProductModel[];
  contentBundle?: ContentBundle;
  contentSoftwarePlan?: ContentSoftwarePlan;
  contentProducts?: ContentProduct[];
  solutionTitle?: string;
}): ICartModel => {
  const {
    id,
    name,
    includedProducts,
    recommendedAddons,
    baseMonthlyPrice,
    additionalDevicePrice,
    keyedInRate,
    inPersonRate,
    softwarePlan: software,
    installmentPlans: ips,
    orderType,
  } = currentPricings;
  const transactions = {
    inPerson: { cost: inPersonRate.fixedAmount.value, percentage: inPersonRate.percentage },
    keyedIn: { cost: keyedInRate.fixedAmount.value, percentage: keyedInRate.percentage },
  };
  const hardware = includedProducts
    .map(prod => convertIncludedProductToProduct(prod, productsPricings, contentProducts))
    .filter(BooleanFilter);
  const products = [...prev.cart.products, ...accessories];
  const upsell = recommendedAddons
    .map(
      addon =>
        addon.product.isSelfServicePurchaseEnabled &&
        convertAddonToUpsell(addon, productsPricings, contentProducts),
    )
    .filter(BooleanFilter);

  const cartMetadata: ICartMetadataModel = {
    id,
    name: contentBundle?.title || name,
    hardware,
    pricingPageLink: contentBundle?.pricingPageLink,
    solutionTitle,
  };
  const softwarePlan: ISoftwarePlanModel = {
    id: software.id,
    name: contentSoftwarePlan?.name || software.name,
    monthly: baseMonthlyPrice.value,
    transactions,
    additionalDevicePrice: additionalDevicePrice?.value,
    imageURL: contentSoftwarePlan?.image?.file?.url ?? null,
  };
  const installmentPlans: IInstallmentPlanModel[] = !ips
    ? []
    : Object.keys(ips).map((key: Installments) => ({
        id: key,
        months: INSTALLMENT_LENGTHS[key],
        cost: ips[key].value,
      }));

  return updateCheckout({
    ...prev,
    cart: { cartMetadata, softwarePlan, products, upsell, orderType },
    checkout: { ...prev.checkout, installmentPlans },
  });
};
